
import { ReactNode, JSX, createContext } from "react";
import { forwardRef } from "react";
import { useContext, useMemo } from "react";

import { FitOptions, getSrcSet, type QualityOptions } from "./CustomImage";

interface Context {
  preload?: boolean;
}

const Context = createContext<Context>({
  preload: false,
});

type SourceProps = Omit<
  React.JSX.IntrinsicElements["source"],
  "width" | "height" | "preload"
> & {
  src: string;
  /** @description Improves Web Vitals (CLS|LCP) */
  width: number;
  /** @description Improves Web Vitals (CLS|LCP) */
  height?: number;
  /** @description Web Vitals (LCP). Adds a link[rel="preload"] tag in head. Use one preload per page for better performance */
  preload?: boolean;
  /** @description Improves Web Vitals (LCP). Use high for LCP image. Auto for other images */
  fetchPriority?: "high" | "low" | "auto";
  /** @description Object-fit */
  fit?: FitOptions;

  factors?: number[];
  /** @description Nivel de compressao. Use "high" em banners com texto. */
  quality?: QualityOptions;
};

export const CustomSource = forwardRef<HTMLSourceElement, SourceProps>(
  (props, ref) => {
    const { preload } = useContext(Context);
    // Custom props are not valid <source> attributes: keep them out of the spread.
    const {
      factors: _factors,
      quality: _quality,
      fit: _fit,
      preload: _preload,
      fetchPriority: _fetchPriority,
      ...sourceProps
    } = props;

    const srcSet = getSrcSet(
      props.src,
      props.width,
      props.height,
      props.fit,
      props.factors,
      props.quality
    );

    const isSingleFactor = (props.factors ?? [1, 2]).length <= 1;

    const linkProps = {
      imagesrcset: isSingleFactor ? undefined : srcSet,
      imagesizes: props.sizes,
      fetchpriority: props.fetchPriority,
      media: props.media,
    };

    return (
      <>
        {preload && (
          <>
            <link as="image" rel="preload" href={props.src} {...linkProps} />
          </>
        )}
        <source
          {...(sourceProps as React.JSX.IntrinsicElements["source"])}
          src={undefined} // Avoid deprecated api lighthouse warning
          srcSet={isSingleFactor ? undefined : srcSet}
          // srcSet={srcSet}
          ref={ref}
        />
      </>
    );
  }
);

type Props = Omit<React.JSX.IntrinsicElements["picture"], "preload"> & {
  children: ReactNode;
  preload?: boolean;
};

export const CustomPicture = forwardRef<HTMLPictureElement, Props>(
  ({ children, preload, ...props }, ref) => {
    const value = useMemo(() => ({ preload }), [preload]);

    return (
      <Context.Provider value={value}>
        <picture {...props} ref={ref}>
          {children}
        </picture>
      </Context.Provider>
    );
  }
);
